import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanBarcode, Send, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, OnlineOrder, UpdateOrderStatusRequest } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { Button, IconTile, Input, Sheet } from '@/components/ui';
import { refreshOnlineOrderSummary } from './useOnlineOrderSummary';

export type OrderAction = { status: 'READY' | 'SENT' | 'DELIVERED' } | { complete: true };
export interface OrderActionItem { label: string; /** Full-sentence label for the detail footer */ longLabel?: string; action: OrderAction; variant: 'primary' | 'outline' }

export const isPickup = (order: OnlineOrder) => !!order.deliveryType?.toUpperCase().includes('PICKUP');

/** Status transitions shared by the board cards and the detail page. */
export function useOnlineOrderActions(shopId: number, onChanged: (updated: OnlineOrder) => void) {
    const { t } = useTranslation();
    const [pending, setPending] = useState<{ order: OnlineOrder; action: OrderAction } | null>(null);
    const [airwaybill, setAirwaybill] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const actionsFor = (order: OnlineOrder): OrderActionItem[] => {
        const s = order.transactionStatus;
        if (s === 'PAID') return [{ label: t('onlineOrders.markReady'), longLabel: t('onlineOrders.markReadyLong'), action: { status: 'READY' }, variant: 'primary' }];
        if (s === 'READY') return isPickup(order)
            ? [{ label: t('onlineOrders.handover'), action: { complete: true }, variant: 'outline' }]
            : [{ label: t('onlineOrders.send'), action: { status: 'SENT' }, variant: 'outline' }];
        if (s === 'SENT') return [{ label: t('onlineOrders.markDelivered'), action: { status: 'DELIVERED' }, variant: 'outline' }];
        if (s === 'DELIVERED') return [{ label: t('onlineOrders.complete'), action: { complete: true }, variant: 'primary' }];
        return [];
    };

    const request = (order: OnlineOrder, action: OrderAction) => {
        setAirwaybill(order.airwaybill ?? '');
        setPending({ order, action });
    };

    const run = async () => {
        if (!pending) return;
        const { order, action } = pending;
        if ('status' in action && action.status === 'SENT' && !airwaybill.trim()) {
            toast.error(t('onlineOrders.airwaybillRequired'));
            return;
        }
        setSubmitting(true);
        try {
            let updated: OnlineOrder;
            if ('complete' in action) {
                updated = (await api.put<ApiResponse<OnlineOrder>>(API_ENDPOINTS.ONLINE_ORDER_COMPLETE(shopId, order.transactionId))).data.data;
            } else {
                const body: UpdateOrderStatusRequest = { status: action.status, airwaybill: action.status === 'SENT' ? airwaybill.trim() : null };
                updated = (await api.put<ApiResponse<OnlineOrder>>(API_ENDPOINTS.ONLINE_ORDER_STATUS(shopId, order.transactionId), body)).data.data;
            }
            setPending(null);
            toast.success(t('onlineOrders.updated'));
            onChanged({ ...order, ...updated });
            refreshOnlineOrderSummary(shopId);
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return { pending, airwaybill, setAirwaybill, submitting, actionsFor, request, run, cancel: () => setPending(null) };
}

export type OnlineOrderActions = ReturnType<typeof useOnlineOrderActions>;

/** 12c airwaybill sheet (for SENT) or a plain confirm for the other transitions. */
export const OrderActionSheet: React.FC<{ actions: OnlineOrderActions; onScan?: () => void }> = ({ actions, onScan }) => {
    const { t } = useTranslation();
    const { pending, airwaybill, setAirwaybill, submitting, run, cancel } = actions;
    const isSend = !!pending && 'status' in pending.action && pending.action.status === 'SENT';
    return (
        <Sheet
            isOpen={pending !== null}
            onClose={cancel}
            size="md"
            showCloseButton={false}
            header={isSend ? (
                <div className="mb-3 flex items-start gap-3">
                    <IconTile size={48} tone="brandSoft"><Truck /></IconTile>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-[22px] font-black leading-tight">{t('onlineOrders.airwaybill')}</h2>
                        <p className="text-sm text-ink-secondary">{t('onlineOrders.awbHint')}</p>
                    </div>
                </div>
            ) : <h2 className="mb-3 text-lg font-black">{t('onlineOrders.confirmTitle')}</h2>}
            footer={
                <div className="grid grid-cols-[1fr_1.4fr] gap-2.5">
                    <Button variant="ghost" size="lg" fullWidth onClick={cancel} disabled={submitting}>{t('cart.cancel')}</Button>
                    <Button variant="primary" size="lg" fullWidth icon={isSend ? <Send /> : undefined} onClick={run} loading={submitting}>{isSend ? t('onlineOrders.send') : t('cart.confirm')}</Button>
                </div>
            }
        >
            {pending && (
                <div className="flex flex-col gap-3">
                    <div className="rounded-md bg-surface-sunken px-3.5 py-3 text-[13px] text-ink-secondary">
                        <strong className="text-ink-primary">{pending.order.transactionId}</strong> · {pending.order.recipientName || pending.order.users?.fullname || '-'}{pending.order.courierName ? ` · ${[pending.order.courierName, pending.order.courierService].filter(Boolean).join(' ')}` : ''}
                    </div>
                    {isSend ? (
                        <Input
                            fieldSize="lg" numeric value={airwaybill} onChange={(e) => setAirwaybill(e.target.value)} autoFocus required
                            placeholder={t('onlineOrders.airwaybill')} wrapperClassName="!border-2 !border-accent-500 shadow-focus" className="!text-lg tracking-[.03em]"
                            rightIcon={onScan ? <button type="button" onClick={onScan} aria-label={t('catalog.scanBarcode')} className="text-ink-brand"><ScanBarcode /></button> : undefined}
                        />
                    ) : <p className="text-sm text-ink-secondary">{t('onlineOrders.confirmBody')}</p>}
                </div>
            )}
        </Sheet>
    );
};
